import _ from 'underscore';
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input, Select } from '@bingads-webui-react/primitive-validation';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';

import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { TaxTypeFormPropTypes } from '../shapes';
import { InputTaxFieldId, requiredFeatures, FileScenarioMapping } from '../constants';
import { registerTaxform } from '../taxform-store';
import { withViewTaxFormWithFilledEmptyTaxValue, getHelpPopupTopicFromTaxSchema, withTaxFormByPilot, getTaxTypeLabel, getPilotStatus } from '../utils';
import { CustomValidateItems } from '../validator';
import { EmptyTaxForm } from './empty-taxform';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { CanadaViewTaxForm } from './viewtaxforms/canada-viewtaxform';
import { useValidationI18n } from '../hooks/use-i18n';
import { TaxCertificateFilePicker } from '../controls/tax-certificate-file-picker';

const { TaxLabelFormat, TaxType, VatStatusEnum, TaxExemptionCertVerifiedResult } = Constants;
const {
  isCanadaGSTNumberValid,
  isCanadaBCPSTNumberValid,
  isCanadaMBPSTNumberValid,
  isCanadaSKPSTNumberValid,
  normalizeTaxExemptionCertVerifiedResult,
  getRequiredForCustomerClaimTaxExemption,
} = TaxUtils;

const getCanadaTaxSchema = () => ({
  name: 'Canada Taxation Validation Schema',
  type: 'object',
  properties: {
    [TaxType.GSTNumber]: {
      type: 'string',
      required: false,
      helpPopupTopic: 'pop_BA_Billing_GST_HST_Number',
    },
    [TaxType.PSTNumber]: {
      type: 'string',
      required: false,
      helpPopupTopic: 'pop_BA_Accounts_TaxRegistrationID',
    },
    IsTaxExemptionCertVerified: {
      // type is not bool because we have 3 status: Approve('True'), Reject('False'), Not set(null).
      // We use the `convertor` on the Select component to parse the string to the optional(bool).
      type: 'string',
    },
  },
});

const stateOrProvinceMapping = {
  BC: {
    isValid: isCanadaBCPSTNumberValid,
    validationMessage: CustomValidateItems.CanadaBCPSTRegistrationNumberFormatValidation,
  },
  MB: {
    isValid: isCanadaMBPSTNumberValid,
    validationMessage: CustomValidateItems.CanadaMBPSTRegistrationNumberFormatValidation,
  },
  SK: {
    isValid: isCanadaSKPSTNumberValid,
    validationMessage: CustomValidateItems.CanadaSKQSTRegistrationNumberFormatValidation,
  },
};

const shouldDisplayTaxCertifications = taxCertifications => !!_.result(taxCertifications[0], 'displayName');

const CanadaTaxForm = ({
  i18n,
  alwaysShowError,
  data,
  disabled,
  onValidate,
  showLineByLineFormGroup,
  taxItem = {},
  isXandrOnboarding,
  showTaxCertLink,
  showTaxApprovalForm,
  taxCertification,
  mtTaxCertification,
  urlBuilder,
  permissions,
}) => {
  const isCanadaTaxCertificateEnabled = getPilotStatus(requiredFeatures.XandrCanadaTaxCertificate, permissions) && isXandrOnboarding; // Currently only enable Canada tax exemption certificate for Xandr onboarding flow
  const validationI18n = useValidationI18n(i18n);
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const CanadaTaxSchema = getCanadaTaxSchema();
  const labelFormat = TaxLabelFormat.RequiredForBusiness;
  const [certificationsState, setCertificationsState] = useState({
    certifications: [],
    isValid: false,
  });
  const [taxData, setTaxData] = useState({ data, isValid: false });
  const isTaxCertPendingReview = mtTaxCertification.status === VatStatusEnum.Pending;
  const { taxCertifications: prefilledTaxCertifications } = taxCertification || {};
  const prefilledDisplayCertifications = shouldDisplayTaxCertifications(prefilledTaxCertifications) ? prefilledTaxCertifications : [];
  const additionalFormData = useMemo(() => ({ scenario: FileScenarioMapping.CA }), []);

  const GSTValidator = useCallback((value, defaultValidator) => {
    const validationMessages = defaultValidator(value);
    if (!_.isEmpty(validationMessages)) {
      return validationMessages;
    }
    return !isCanadaGSTNumberValid(value) ? [i18n.getString(CustomValidateItems.CanadaGSTRegistrationNumberFormatValidation)] : [];
  }, [i18n]);

  const PSTValidator = useCallback((value, defaultValidator) => {
    const validationMessages = defaultValidator(value);
    if (!_.isEmpty(validationMessages)) {
      return validationMessages;
    }

    if (_.isFunction(taxItem.isValid) && taxItem.validationMessage) {
      return !taxItem.isValid(value) ? [i18n.getString(taxItem.validationMessage)] : [];
    }

    return [];
  }, [i18n, taxItem]);

  const onTaxFormValidate = useCallback(({ data: pstTaxData, isValid }) => {
    setTaxData({ data: pstTaxData, isValid });
  }, []);

  const onCertificationsChange = useCallback(({ certifications, isValid }) => {
    setCertificationsState({
      certifications,
      isValid,
    });
  }, []);

  useEffect(() => {
    if (isCanadaTaxCertificateEnabled) {
      const taxCertifications = _.isEmpty(certificationsState.certifications) ? mtTaxCertification.taxCertifications : certificationsState.certifications;
      const isSuccessfullyUploaded = _.every(taxCertifications, cert => cert && cert.guid);
      const isValid = taxData.isValid && certificationsState.isValid && isSuccessfullyUploaded;

      onValidate({
        data: taxData.data,
        taxCertification: {
          taxCertifications,
        },
        isValid,
      });
    } else {
      onValidate({
        data: taxData.data,
        isValid: taxData.isValid,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificationsState, taxData, isCanadaTaxCertificateEnabled]);

  return (
    <div>
      <ValidationGroup
        validationProps={{
          i18n: validationI18n,
          onValidate: onTaxFormValidate,
          data,
          schema: CanadaTaxSchema,
        }}
      >
        <CustomizedFormGroup
          isStatic
          label={getTaxTypeLabel(TaxType.GSTNumber, i18n, { labelFormat })}
          helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: CanadaTaxSchema, taxType: TaxType.GSTNumber })}
        >
          <Input
            id={InputTaxFieldId(TaxType.GSTNumber)}
            validationProps={{
              field: TaxType.GSTNumber,
              alwaysShowError,
              validator: GSTValidator,
            }}
            disabled={disabled}
          />
        </CustomizedFormGroup>
        <CustomizedFormGroup
          isStatic
          label={getTaxTypeLabel(TaxType.PSTNumber, i18n, { labelFormat })}
          helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: CanadaTaxSchema, taxType: TaxType.PSTNumber })}
        >
          <Input
            id={InputTaxFieldId(TaxType.PSTNumber)}
            validationProps={{
              field: TaxType.PSTNumber,
              alwaysShowError,
              validator: PSTValidator,
            }}
            disabled={disabled}
          />
        </CustomizedFormGroup>
        {isCanadaTaxCertificateEnabled && (
          <>
            <TaxCertificateFilePicker
              i18n={i18n}
              certifications={prefilledDisplayCertifications}
              mtTaxCertification={mtTaxCertification}
              onCertificationsChange={onCertificationsChange}
              showLineByLineFormGroup={showLineByLineFormGroup}
              disabled={disabled}
              urlBuilder={urlBuilder}
              showTaxCertLink={showTaxCertLink}
              taxCertLabel={getRequiredForCustomerClaimTaxExemption(i18n.getString(_TL_('Tax exemption certificate')), i18n)}
              additionalFormData={additionalFormData}
            />
            <InlineInfoAlert>{i18n.getString(_TL_('Failure to provide your tax exemption certificate may result in tax being applied to your invoice.'))}</InlineInfoAlert>
          </>
        )}
        {isCanadaTaxCertificateEnabled && showTaxApprovalForm && (
          <CustomizedFormGroup
            isStatic
            label={i18n.getString(_TL_('Tax certificate validation result'))}
          >
            <Select
              id="TaxCertificationValidationResult"
              disabled={disabled || !isTaxCertPendingReview}
              validationProps={{
                field: 'IsTaxExemptionCertVerified',
                alwaysShowError,
                convertor: {
                  fromModel: value => normalizeTaxExemptionCertVerifiedResult(value) || 'NotSet',
                  toModel: normalizeTaxExemptionCertVerifiedResult,
                },
              }}
            >
              <option value="NotSet">{i18n.getString(_TL_('(Not set)'))}</option>
              <option value={TaxExemptionCertVerifiedResult.Accept}>{i18n.getString(_TL_('Approve'))}</option>
              <option value={TaxExemptionCertVerifiedResult.Reject}>{i18n.getString(_TL_('Reject'))}</option>
            </Select>
          </CustomizedFormGroup>
        )}
      </ValidationGroup>
    </div>
  );
};

CanadaTaxForm.propTypes = TaxTypeFormPropTypes;

const GetCanadaProvinceTaxForm = ({ taxItem }) => {
  const customizedTaxForm = props => <CanadaTaxForm {...props} taxItem={taxItem} />;

  customizedTaxForm.propTypes = TaxTypeFormPropTypes;

  return customizedTaxForm;
};

const pilotFlag = requiredFeatures.ABLCanadaTax;
const ViewTaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: CanadaViewTaxForm, DefaultForm: EmptyViewTaxForm });

_.each(stateOrProvinceMapping, (taxItem, stateOrProvinceCode) => {
  const TaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: GetCanadaProvinceTaxForm({ taxItem }), DefaultForm: EmptyTaxForm });
  registerTaxform(`/CA/${stateOrProvinceCode}/`, TaxForm, withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm, getTaxSchema: getCanadaTaxSchema }));
});
